import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { customerApi } from '../../services/smartBillApi';
import './CustomerSearch.css';

const CustomerSearch = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [sortBy, setSortBy] = useState('last_visit'); // 'name', 'phone', 'last_visit'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  // Load all customers on component mount
  useEffect(() => {
    fetchAllCustomers();
  }, []);

  const fetchAllCustomers = async () => {
    setLoading(true);
    try {
      const response = await customerApi.getAll('');
      if (response.success && response.data) {
        setCustomers(response.data);
      } else {
        toast.error('Failed to load customers');
        setCustomers([]);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      toast.error('Error loading customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort customers
  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = [...customers];

    // Apply search filter
    if (searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase();
      filtered = filtered.filter(customer => {
        const name = (customer.name || '').toLowerCase();
        const phone = (customer.phone || '').replace(/\D/g, '');
        const profession = (customer.profession || '').toLowerCase();
        const address = (customer.address || '').toLowerCase();
        
        return name.includes(searchLower) ||
               phone.includes(searchFilter.replace(/\D/g, '')) ||
               profession.includes(searchLower) ||
               address.includes(searchLower);
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'phone':
          aValue = (a.phone || '').replace(/\D/g, '');
          bValue = (b.phone || '').replace(/\D/g, '');
          break;
        case 'last_visit':
          aValue = a.last_visit ? new Date(a.last_visit).getTime() : 0;
          bValue = b.last_visit ? new Date(b.last_visit).getTime() : 0;
          break;
        default:
          aValue = a.name || '';
          bValue = b.name || '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  }, [customers, searchFilter, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleCustomerClick = (customer) => {
    const cleanPhone = (customer.phone || '').replace(/\D/g, '').slice(-10);
    const customerName = customer.name || 'Customer';
    
    if (cleanPhone.length === 10) {
      navigate(
        `/customer/dashboard?phone=${cleanPhone}&name=${encodeURIComponent(customerName)}`
      );
    } else {
      toast.error('Invalid phone number for this customer');
    }
  };

  const formatPhone = (phone) => {
    const clean = (phone || '').replace(/\D/g, '');
    if (clean.length === 10) {
      return `${clean.slice(0, 5)} ${clean.slice(5)}`;
    }
    return clean;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'Invalid';
    }
  };

  return (
    <div className="customer-search-page">
      <div className="search-container">
        <div className="search-header">
          <h1>
            <i className="fas fa-users"></i> Customer Search
          </h1>
          <p>Browse all customers - Click on any record to view their dashboard</p>
        </div>

        {/* Filter and Sort Controls */}
        <div className="controls-section">
          <div className="filter-box">
            <i className="fas fa-search filter-icon"></i>
            <input
              type="text"
              className="filter-input"
              placeholder="Search by name, phone, profession, or address..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              autoFocus
            />
            {searchFilter && (
              <button
                className="clear-filter-btn"
                onClick={() => setSearchFilter('')}
                title="Clear filter"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          <div className="sort-controls">
            <label>Sort by:</label>
            <select
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="last_visit">Last Visit</option>
              <option value="name">Name</option>
              <option value="phone">Phone</option>
            </select>
            <button
              className="sort-order-btn"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              <i className={`fas fa-sort-${sortOrder === 'asc' ? 'amount-up' : 'amount-down'}`}></i>
            </button>
          </div>
        </div>

        {/* Customer List/Table */}
        <div className="customers-section">
          {loading ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading customers...</p>
            </div>
          ) : filteredAndSortedCustomers.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-users-slash"></i>
              <p>
                {searchFilter
                  ? 'No customers found matching your search'
                  : 'No customers found'}
              </p>
            </div>
          ) : (
            <>
              <div className="customers-count">
                Showing {filteredAndSortedCustomers.length} of {customers.length} customers
              </div>
              <div className="customers-table-container">
                <table className="customers-table">
                  <thead>
                    <tr>
                      <th
                        className={sortBy === 'name' ? `sortable active ${sortOrder}` : 'sortable'}
                        onClick={() => handleSort('name')}
                      >
                        Name
                        {sortBy === 'name' && (
                          <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th
                        className={sortBy === 'phone' ? `sortable active ${sortOrder}` : 'sortable'}
                        onClick={() => handleSort('phone')}
                      >
                        Phone
                        {sortBy === 'phone' && (
                          <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th>Profession</th>
                      <th>Address</th>
                      <th
                        className={sortBy === 'last_visit' ? `sortable active ${sortOrder}` : 'sortable'}
                        onClick={() => handleSort('last_visit')}
                      >
                        Last Visit
                        {sortBy === 'last_visit' && (
                          <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedCustomers.map((customer) => (
                      <tr
                        key={customer.id || customer.phone}
                        className="customer-row"
                        onClick={() => handleCustomerClick(customer)}
                      >
                        <td className="customer-name">
                          <i className="fas fa-user-circle"></i>
                          {customer.name || 'N/A'}
                        </td>
                        <td className="customer-phone">
                          <i className="fas fa-phone"></i>
                          {formatPhone(customer.phone)}
                        </td>
                        <td className="customer-profession">
                          {customer.profession || '-'}
                        </td>
                        <td className="customer-address">
                          {customer.address ? (
                            <>
                              {customer.address}
                              {customer.city && `, ${customer.city}`}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="customer-last-visit">
                          {formatDate(customer.last_visit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerSearch;


